import { useEffect, useRef, useState } from 'react';

/**
 * Small ? control that explains why a rule table matched, and reveals the hit in the editor.
 */
export default function RuleSetSearchHitHint({ hits = [], onReveal }) {
    const [isOpen, setIsOpen] = useState(false);
    const panelRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return undefined;

        const handlePointerDown = (event) => {
            if (panelRef.current && !panelRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setIsOpen(false);
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    if (!hits.length) return null;

    const summary = hits
        .map(hit => `${hit.label}: ${hit.items.join(', ')}`)
        .join('\n');

    const reveal = (revealedHits) => {
        if (typeof onReveal === 'function') onReveal(revealedHits);
    };

    return (
        <div
            ref={panelRef}
            style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
        >
            <button
                type="button"
                onClick={(event) => {
                    event.stopPropagation();
                    setIsOpen(prev => !prev);
                    reveal(hits);
                }}
                aria-label="Why this table matched"
                aria-expanded={isOpen}
                title={summary}
                style={{
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: 'var(--text-secondary)',
                    fontSize: '0.85rem'
                }}
            >
                <i className="fa-regular fa-circle-question"></i>
            </button>
            {isOpen && (
                <div
                    role="dialog"
                    aria-label="Search match details"
                    onClick={(event) => event.stopPropagation()}
                    style={{
                        position: 'absolute',
                        top: 'calc(100% - 0.15rem)',
                        left: '0.75rem',
                        right: '0.75rem',
                        marginTop: '0.25rem',
                        maxHeight: '220px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        background: 'var(--bg-primary)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                        zIndex: 80,
                        padding: '0.65rem 0.75rem',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem',
                        whiteSpace: 'normal'
                    }}
                >
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                        Matched on
                    </div>
                    {hits.map(hit => (
                        <button
                            key={hit.type}
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                reveal([hit]);
                            }}
                            title={`Show ${hit.label.toLowerCase()} in the table`}
                            style={{
                                fontSize: '0.75rem',
                                color: 'var(--text-primary)',
                                textAlign: 'left',
                                background: 'transparent',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                width: '100%',
                                whiteSpace: 'normal',
                                overflow: 'visible'
                            }}
                        >
                            <div style={{ color: 'var(--accent)', marginBottom: '0.15rem', fontWeight: 600 }}>
                                {hit.label}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                                {hit.items.map((item) => (
                                    <div key={item} style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                        {item}
                                    </div>
                                ))}
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
